import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Box,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  useTheme
} from '@mui/material';
import {
  Chat as ChatIcon,
  Lock as LockIcon,
  Star as StarIcon
} from '@mui/icons-material';
import GameChatDialog from './chat/GameChatDialog';

import SportsbookPreferences from '../services/sportsbookPreferences';
import FavoriteTeamService from '../services/favoriteTeamService';
import { SportIcon } from '../utils/sportIcons';
import { useAuth } from '../contexts/AuthContext';

const LiveGamesList = ({ odds, selectedSport }) => {
  const { currentUser } = useAuth();
  const theme = useTheme();
  
  // Helper function to get default market based on sport
  const getDefaultMarket = () => {
    const isBaseball = selectedSport && (selectedSport.title.includes('MLB') || selectedSport.title.includes('MiLB'));
    return isBaseball ? 'h2h' : 'spreads';
  };

  const [selectedGame, setSelectedGame] = useState(null);
  const [chatDialogOpen, setChatDialogOpen] = useState(false);
  const [globalActiveMarket, setGlobalActiveMarket] = useState(getDefaultMarket()); // Global market selection for all games
  const [sportsbookOrder, setSportsbookOrder] = useState([]);
  const [favoriteTeams, setFavoriteTeams] = useState({});

  // Load sportsbook order from preferences on component mount
  useEffect(() => {
    const loadSportsbookOrder = () => {
      const order = SportsbookPreferences.getSportsbookNamesSync();
      setSportsbookOrder(order);
    };

    loadSportsbookOrder();

    // Listen for sportsbook order changes (when user updates settings)
    const handleOrderChange = () => {
      loadSportsbookOrder();
    };

    window.addEventListener('sportsbookOrderChanged', handleOrderChange);

    // Cleanup event listener
    return () => {
      window.removeEventListener('sportsbookOrderChanged', handleOrderChange);
    };
  }, []);

  // Update default market when sport changes
  useEffect(() => {
    const getDefaultMarket = () => {
      const isBaseball = selectedSport && (selectedSport.title.includes('MLB') || selectedSport.title.includes('MiLB'));
      return isBaseball ? 'h2h' : 'spreads';
    };
    
    setGlobalActiveMarket(getDefaultMarket());
  }, [selectedSport]);

  // Load favorite teams
  useEffect(() => {
    const loadFavoriteTeams = async () => {
      try {
        const teams = await FavoriteTeamService.getFavoriteTeams();
        setFavoriteTeams(teams);
      } catch (error) {
        console.error('Error loading favorite teams:', error);
      }
    };

    loadFavoriteTeams();
  }, [currentUser]);

  // Market types configuration - dynamic based on sport
  const getMarketTypes = (sportTitle) => {
    const isBaseball = sportTitle && (sportTitle.includes('MLB') || sportTitle.includes('MiLB'));
    const isHockey = sportTitle && sportTitle.includes('NHL');
    
    let spreadLabel = 'Spread';
    if (isBaseball) {
      spreadLabel = 'Runline';
    } else if (isHockey) {
      spreadLabel = 'Puckline';
    }
    
    return {
      h2h: { label: 'Moneyline', key: 'h2h' },
      spreads: { label: spreadLabel, key: 'spreads' },
      totals: { label: 'Total', key: 'totals' }
    };
  };

  const handleGlobalTabChange = (event, newValue) => {
    setGlobalActiveMarket(newValue);
  };

  const handleChatClick = (game, event) => {
    event.stopPropagation();
    setSelectedGame(game);
    setChatDialogOpen(true);
  };

  const handleCloseChatDialog = () => {
    setChatDialogOpen(false);
    setSelectedGame(null);
  };

  // Simple function to get sportsbook homepage URLs
  const getSportsbookHomepage = (bookmakerTitle) => {
    const homepages = {
      'DraftKings': 'https://sportsbook.draftkings.com',
      'FanDuel': 'https://sportsbook.fanduel.com',
      'BetMGM': 'https://sports.betmgm.com',
      'Caesars': 'https://www.caesars.com/sportsbook',
      'PointsBet': 'https://pointsbet.com',
      'BetRivers': 'https://ny.betrivers.com/?page=sportsbook',
      'Unibet': 'https://www.unibet.com/betting',
      'Barstool': 'https://www.barstoolsportsbook.com',
      'TwinSpires': 'https://www.twinspires.com/sportsbook',
      'William Hill': 'https://www.williamhill.com/us',
      'BetUS': 'https://www.betus.com.pa/sportsbook',
      'BetAnySports': 'https://www.betanysports.com',
      'Bovada': 'https://www.bovada.lv/sports',
      'MyBookie': 'https://mybookie.ag/sportsbook',
      'XBet': 'https://www.xbet.ag/sportsbook',
      'LowVig': 'https://www.lowvig.ag/sports',
      'BetOnline': 'https://www.betonline.ag/sportsbook',
      'Fanatics': 'https://www.fanaticssportsbook.com'
    };
    
    return homepages[bookmakerTitle] || `https://www.google.com/search?q=${encodeURIComponent(bookmakerTitle + ' sportsbook')}`;
  };

  const handleOddsClick = (outcome, market, bookmaker, event, gameIndex) => {
    event.stopPropagation();

    // Open sportsbook homepage in new tab
    const homepage = getSportsbookHomepage(bookmaker.title);
    window.open(homepage, '_blank', 'noopener,noreferrer');
  };

  // Get bookmakers that have odds for a specific game and market
  const getBookmakersForGame = (game, marketType = 'h2h') => {
    const bookmakers = [];
    game.bookmakers?.forEach(bookmaker => {
      const market = bookmaker.markets?.find(m => m.key === marketType);
      // Only include bookmakers that have outcomes for the specified market
      if (market?.outcomes && market.outcomes.length >= 2) {
        // For moneyline, check that we have valid odds for both teams
        if (marketType === 'h2h') {
          const homeTeamOutcome = market.outcomes.find(outcome => outcome.name === game.home_team);
          const awayTeamOutcome = market.outcomes.find(outcome => outcome.name === game.away_team);

          const hasValidOdds = homeTeamOutcome &&
            awayTeamOutcome &&
            homeTeamOutcome.price &&
            typeof homeTeamOutcome.price === 'number' &&
            awayTeamOutcome.price &&
            typeof awayTeamOutcome.price === 'number';

          if (hasValidOdds) {
            bookmakers.push(bookmaker.title);
          }
        }
        // For spreads, check that we have valid odds and points for both teams
        else if (marketType === 'spreads') {
          const homeTeamOutcome = market.outcomes.find(outcome => outcome.name === game.home_team);
          const awayTeamOutcome = market.outcomes.find(outcome => outcome.name === game.away_team);

          const hasValidSpreads = homeTeamOutcome &&
            awayTeamOutcome &&
            homeTeamOutcome.price &&
            typeof homeTeamOutcome.price === 'number' &&
            awayTeamOutcome.price &&
            typeof awayTeamOutcome.price === 'number' &&
            typeof homeTeamOutcome.point === 'number' &&
            typeof awayTeamOutcome.point === 'number';

          if (hasValidSpreads) {
            bookmakers.push(bookmaker.title);
          }
        }
        // For totals, check that we have valid odds and points for over/under
        else if (marketType === 'totals') {
          const overOutcome = market.outcomes.find(outcome => outcome.name === 'Over');
          const underOutcome = market.outcomes.find(outcome => outcome.name === 'Under');

          const hasValidTotals = overOutcome &&
            underOutcome &&
            overOutcome.price &&
            typeof overOutcome.price === 'number' &&
            underOutcome.price &&
            typeof underOutcome.price === 'number' &&
            typeof overOutcome.point === 'number' &&
            typeof underOutcome.point === 'number';

          if (hasValidTotals) {
            bookmakers.push(bookmaker.title);
          }
        }
      }
    });

    const sortedBookmakers = bookmakers.sort((a, b) => {
      // Safety check: if sportsbookOrder is not loaded yet, sort alphabetically
      if (!sportsbookOrder || sportsbookOrder.length === 0) {
        return a.localeCompare(b);
      }

      const indexA = sportsbookOrder.indexOf(a);
      const indexB = sportsbookOrder.indexOf(b);

      if (indexA === -1 && indexB === -1) return a.localeCompare(b); // Sort alphabetically if not in list
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;

      return indexA - indexB;
    });

    return sortedBookmakers.slice(0, 8); // Limit to 8 for mobile
  };

  const getOddsForTeam = (game, bookmakerTitle, teamName, marketType = 'h2h') => {
    const bookmaker = game.bookmakers?.find(b => b.title === bookmakerTitle);
    const market = bookmaker?.markets?.find(m => m.key === marketType);
    
    let outcomeToFind = teamName;
    
    // For totals market, map team names to Over/Under
    if (marketType === 'totals') {
      if (teamName === game.away_team) {
        outcomeToFind = 'Over';
      } else if (teamName === game.home_team) {
        outcomeToFind = 'Under';
      }
    }
    
    const outcome = market?.outcomes?.find(o => o.name === outcomeToFind);
    // Return null if outcome doesn't have valid price data
    return outcome && outcome.price && typeof outcome.price === 'number' ? outcome : null;
  };

  const getBestOdds = (game, teamName, marketType = 'h2h') => {
    let bestOdds = null;
    let bestPrice = teamName === game.home_team ? -Infinity : Infinity;

    // For totals, we want the best price for Over/Under regardless of home/away logic
    if (marketType === 'totals') {
      bestPrice = -Infinity; // For totals, higher odds are better for both Over and Under
    }

    game.bookmakers?.forEach(bookmaker => {
      const market = bookmaker.markets?.find(m => m.key === marketType);
      
      let outcomeToFind = teamName;
      
      // For totals market, map team names to Over/Under
      if (marketType === 'totals') {
        if (teamName === game.away_team) {
          outcomeToFind = 'Over';
        } else if (teamName === game.home_team) {
          outcomeToFind = 'Under';
        }
      }
      
      const outcome = market?.outcomes?.find(o => o.name === outcomeToFind);
      if (outcome && outcome.price && typeof outcome.price === 'number') {
        let isBetter = false;

        if (marketType === 'totals' || marketType === 'spreads') {
          // For totals and spreads, higher odds (closer to positive or less negative) are always better
          isBetter = outcome.price > bestPrice;
        } else {
          // For moneyline, logic depends on whether it's home or away team
          isBetter = teamName === game.home_team ?
            outcome.price > bestPrice : outcome.price < Math.abs(bestPrice);
        }

        if (isBetter) {
          bestOdds = { bookmaker: bookmaker.title, outcome };
          bestPrice = outcome.price;
        }
      }
    });

    return bestOdds;
  };

  // Helper function to check if a team is a favorite
  const isFavoriteTeam = (teamName) => {
    const sportTitle = selectedSport?.title || '';
    let sportKey = '';
    
    if (sportTitle.includes('NFL')) sportKey = 'NFL';
    else if (sportTitle.includes('NBA')) sportKey = 'NBA';
    else if (sportTitle.includes('MLB')) sportKey = 'MLB';
    else if (sportTitle.includes('NHL')) sportKey = 'NHL';
    
    const favoriteTeam = favoriteTeams[sportKey];
    return favoriteTeam === teamName;
  };

  // Function to render table content based on market type
  const renderTableContent = (game, gameIndex, marketType, gameBookmakers) => {
    if (marketType === 'totals') {
      return (
        <>
          {/* Away Team Row (Over) */}
          <TableRow
            sx={{
              '&:hover': { backgroundColor: 'action.hover' },
              borderBottom: 'none'
            }}
          >
            <TableCell
              sx={{
                fontWeight: 600,
                borderBottom: 'none',
                py: 1
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: 'primary.main'
                  }}
                />
                {game.away_team}
                {isFavoriteTeam(game.away_team) && (
                  <StarIcon sx={{ color: 'warning.main', fontSize: 16 }} />
                )}
              </Box>
            </TableCell>
            {gameBookmakers.map((bookmakerTitle) => {
              const outcome = getOddsForTeam(game, bookmakerTitle, game.away_team, marketType);
              const bestOdds = getBestOdds(game, game.away_team, marketType);
              const isBest = bestOdds?.bookmaker === bookmakerTitle;

              return (
                <TableCell
                  key={bookmakerTitle}
                  align="center"
                  sx={{
                    borderBottom: 'none',
                    px: 0.5,
                    py: 1
                  }}
                >
                  {outcome ? (
                    <Button
                      variant={isBest ? "contained" : "outlined"}
                      size="small"
                      onClick={(e) => handleOddsClick(outcome, marketType, { title: bookmakerTitle }, e, gameIndex)}
                      sx={{
                        minWidth: 45,
                        fontSize: '0.7rem',
                        py: 0.5,
                        px: 1,
                        textTransform: 'none',
                        backgroundColor: isBest ? 'warning.main' : 'transparent',
                        borderColor: isBest ? 'warning.main' : 'grey.300',
                        color: isBest ? 'white' : 'text.primary',
                        '&:hover': {
                          backgroundColor: isBest ? 'warning.dark' : 'primary.light',
                          color: 'white'
                        }
                      }}
                    >
                      <Box textAlign="center">
                        <Box>{marketType === 'totals' ? `o${outcome.point}` : outcome.point}</Box>
                        <Box>{outcome.price > 0 ? `+${outcome.price}` : outcome.price}</Box>
                      </Box>
                    </Button>
                  ) : (
                    <Typography variant="caption" color="text.disabled">
                      N/A
                    </Typography>
                  )}
                </TableCell>
              );
            })}
          </TableRow>

          {/* Home Team Row (Under) */}
          <TableRow
            sx={{
              '&:hover': { backgroundColor: 'action.hover' }
            }}
          >
            <TableCell
              sx={{
                fontWeight: 600,
                py: 1
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: 'secondary.main'
                  }}
                />
                {game.home_team}
                {isFavoriteTeam(game.home_team) && (
                  <StarIcon sx={{ color: 'warning.main', fontSize: 16 }} />
                )}
              </Box>
            </TableCell>
            {gameBookmakers.map((bookmakerTitle) => {
              const outcome = getOddsForTeam(game, bookmakerTitle, game.home_team, marketType);
              const bestOdds = getBestOdds(game, game.home_team, marketType);
              const isBest = bestOdds?.bookmaker === bookmakerTitle;

              return (
                <TableCell
                  key={bookmakerTitle}
                  align="center"
                  sx={{
                    px: 0.5,
                    py: 1
                  }}
                >
                  {outcome ? (
                    <Button
                      variant={isBest ? "contained" : "outlined"}
                      size="small"
                      onClick={(e) => handleOddsClick(outcome, marketType, { title: bookmakerTitle }, e, gameIndex)}
                      sx={{
                        minWidth: 45,
                        fontSize: '0.7rem',
                        py: 0.5,
                        px: 1,
                        textTransform: 'none',
                        backgroundColor: isBest ? 'warning.main' : 'transparent',
                        borderColor: isBest ? 'warning.main' : 'grey.300',
                        color: isBest ? 'white' : 'text.primary',
                        '&:hover': {
                          backgroundColor: isBest ? 'warning.dark' : 'primary.light',
                          color: 'white'
                        }
                      }}
                    >
                      <Box textAlign="center">
                        <Box>{marketType === 'totals' ? `u${outcome.point}` : outcome.point}</Box>
                        <Box>{outcome.price > 0 ? `+${outcome.price}` : outcome.price}</Box>
                      </Box>
                    </Button>
                  ) : (
                    <Typography variant="caption" color="text.disabled">
                      N/A
                    </Typography>
                  )}
                </TableCell>
              );
            })}
          </TableRow>
        </>
      );
    } else {
      // Standard team-based rows (moneyline and spreads)
      return (
        <>
          {/* Away Team Row */}
          <TableRow
            sx={{
              '&:hover': { backgroundColor: 'action.hover' },
              borderBottom: 'none'
            }}
          >
            <TableCell
              sx={{
                fontWeight: 600,
                borderBottom: 'none',
                py: 1
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: 'primary.main'
                  }}
                />
                {game.away_team}
                {isFavoriteTeam(game.away_team) && (
                  <StarIcon sx={{ color: 'warning.main', fontSize: 16 }} />
                )}
              </Box>
            </TableCell>
            {gameBookmakers.map((bookmakerTitle) => {
              const outcome = getOddsForTeam(game, bookmakerTitle, game.away_team, marketType);
              const bestOdds = getBestOdds(game, game.away_team, marketType);
              const isBest = bestOdds?.bookmaker === bookmakerTitle;

              return (
                <TableCell
                  key={bookmakerTitle}
                  align="center"
                  sx={{
                    borderBottom: 'none',
                    px: 0.5,
                    py: 1
                  }}
                >
                  {outcome ? (
                    <Button
                      variant={isBest ? "contained" : "outlined"}
                      size="small"
                      onClick={(e) => handleOddsClick(outcome, marketType, { title: bookmakerTitle }, e, gameIndex)}
                      sx={{
                        minWidth: 45,
                        fontSize: '0.7rem',
                        py: 0.5,
                        px: 1,
                        backgroundColor: isBest ? 'warning.main' : 'transparent',
                        borderColor: isBest ? 'warning.main' : 'grey.300',
                        color: isBest ? 'white' : 'text.primary',
                        '&:hover': {
                          backgroundColor: isBest ? 'warning.dark' : 'primary.light',
                          color: 'white'
                        }
                      }}
                    >
                      {marketType === 'spreads' && outcome.point !== undefined ? (
                        <Box textAlign="center">
                          <Box>{outcome.point > 0 ? `+${outcome.point}` : outcome.point}</Box>
                          <Box>{outcome.price > 0 ? `+${outcome.price}` : outcome.price}</Box>
                        </Box>
                      ) : (
                        <Box>{outcome.price > 0 ? `+${outcome.price}` : outcome.price}</Box>
                      )}
                    </Button>
                  ) : (
                    <Typography variant="caption" color="text.disabled">
                      N/A
                    </Typography>
                  )}
                </TableCell>
              );
            })}
          </TableRow>

          {/* Home Team Row */}
          <TableRow
            sx={{
              '&:hover': { backgroundColor: 'action.hover' }
            }}
          >
            <TableCell
              sx={{
                fontWeight: 600,
                py: 1
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: 'secondary.main'
                  }}
                />
                {game.home_team}
                {isFavoriteTeam(game.home_team) && (
                  <StarIcon sx={{ color: 'warning.main', fontSize: 16 }} />
                )}
              </Box>
            </TableCell>
            {gameBookmakers.map((bookmakerTitle) => {
              const outcome = getOddsForTeam(game, bookmakerTitle, game.home_team, marketType);
              const bestOdds = getBestOdds(game, game.home_team, marketType);
              const isBest = bestOdds?.bookmaker === bookmakerTitle;

              return (
                <TableCell
                  key={bookmakerTitle}
                  align="center"
                  sx={{
                    px: 0.5,
                    py: 1
                  }}
                >
                  {outcome ? (
                    <Button
                      variant={isBest ? "contained" : "outlined"}
                      size="small"
                      onClick={(e) => handleOddsClick(outcome, marketType, { title: bookmakerTitle }, e, gameIndex)}
                      sx={{
                        minWidth: 45,
                        fontSize: '0.7rem',
                        py: 0.5,
                        px: 1,
                        backgroundColor: isBest ? 'warning.main' : 'transparent',
                        borderColor: isBest ? 'warning.main' : 'grey.300',
                        color: isBest ? 'white' : 'text.primary',
                        '&:hover': {
                          backgroundColor: isBest ? 'warning.dark' : 'primary.light',
                          color: 'white'
                        }
                      }}
                    >
                      {marketType === 'spreads' && outcome.point !== undefined ? (
                        <Box textAlign="center">
                          <Box>{outcome.point > 0 ? `+${outcome.point}` : outcome.point}</Box>
                          <Box>{outcome.price > 0 ? `+${outcome.price}` : outcome.price}</Box>
                        </Box>
                      ) : (
                        <Box>{outcome.price > 0 ? `+${outcome.price}` : outcome.price}</Box>
                      )}
                    </Button>
                  ) : (
                    <Typography variant="caption" color="text.disabled">
                      N/A
                    </Typography>
                  )}
                </TableCell>
              );
            })}
          </TableRow>
        </>
      );
    }
  };

  if (!odds || odds.length === 0) {
    return (
      <Box textAlign="center" mt={4}>
        <Typography variant="h6" color="text.secondary">
          No live games right now
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={1}>
          Games appear here when they're live or starting soon (within 30 minutes)
        </Typography>
      </Box>
    );
  }

  // Filter out games that don't have any valid odds for any market
  const gamesWithOdds = odds.filter(game => {
    const hasMoneyline = getBookmakersForGame(game, 'h2h').length > 0;
    const hasSpreads = getBookmakersForGame(game, 'spreads').length > 0;
    const hasTotals = getBookmakersForGame(game, 'totals').length > 0;
    return hasMoneyline || hasSpreads || hasTotals;
  });

  if (gamesWithOdds.length === 0) {
    return (
      <Box textAlign="center" mt={4}>
        <Typography variant="h6" color="text.secondary">
          No games with odds available
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={1}>
          All games loaded but none have complete betting odds data
        </Typography>
      </Box>
    );
  }

  // Get all available markets across all games
  const getAllAvailableMarkets = (games) => {
    const allMarkets = new Set();
    games.forEach(game => {
      if (getBookmakersForGame(game, 'spreads').length > 0) allMarkets.add('spreads');
      if (getBookmakersForGame(game, 'totals').length > 0) allMarkets.add('totals');
      if (getBookmakersForGame(game, 'h2h').length > 0) allMarkets.add('h2h');
    });
    
    const availableMarkets = Array.from(allMarkets);
    
    // For baseball (MLB/MiLB), order should be: Moneyline, Total, Spread
    const isBaseball = selectedSport && (selectedSport.title.includes('MLB') || selectedSport.title.includes('MiLB'));
    if (isBaseball) {
      const baseballOrder = ['h2h', 'totals', 'spreads'];
      return baseballOrder.filter(market => availableMarkets.includes(market));
    }
    
    // Default order for all other sports: Spread, Total, Moneyline
    const defaultOrder = ['spreads', 'totals', 'h2h'];
    return defaultOrder.filter(market => availableMarkets.includes(market));
  };

  return (
    <Box>
      {/* Global Market Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={globalActiveMarket}
          onChange={handleGlobalTabChange}
          variant="standard"
          sx={{ 
            minHeight: 36,
            '& .MuiTabs-flexContainer': {
              justifyContent: 'flex-start'
            }
          }}
        >
          {getAllAvailableMarkets(gamesWithOdds).map(marketKey => {
            let marketTypes;
            if (selectedSport) {
              marketTypes = getMarketTypes(selectedSport.title);
            } else {
              marketTypes = {
                h2h: { label: 'Moneyline', key: 'h2h' },
                spreads: { label: 'Spread', key: 'spreads' },
                totals: { label: 'Total', key: 'totals' }
              };
            }
            
            return (
              <Tab
                key={marketKey}
                value={marketKey}
                label={marketTypes[marketKey].label}
                sx={{
                  minHeight: 32,
                  py: 0.5,
                  px: 2,
                  fontSize: '0.75rem',
                  minWidth: 'auto',
                  textTransform: 'none'
                }}
              />
            );
          })}
        </Tabs>
      </Box>

      {gamesWithOdds
        .filter(game => getBookmakersForGame(game, globalActiveMarket).length > 0)
        .sort((gameA, gameB) => {
          // Helper function to check if game has favorite team
          const hasFavoriteTeam = (game) => {
            const homeTeam = game.home_team;
            const awayTeam = game.away_team;
            
            // Get sport title for mapping to favorite teams
            const sportTitle = selectedSport?.title || '';
            let sportKey = '';
            
            if (sportTitle.includes('NFL')) sportKey = 'NFL';
            else if (sportTitle.includes('NBA')) sportKey = 'NBA';
            else if (sportTitle.includes('MLB')) sportKey = 'MLB';
            else if (sportTitle.includes('NHL')) sportKey = 'NHL';
            
            const favoriteTeam = favoriteTeams[sportKey];
            return favoriteTeam && (homeTeam === favoriteTeam || awayTeam === favoriteTeam);
          };
          
          const aHasFavorite = hasFavoriteTeam(gameA);
          const bHasFavorite = hasFavoriteTeam(gameB);
          
          // Prioritize games with favorite teams
          if (aHasFavorite && !bHasFavorite) return -1;
          if (!aHasFavorite && bHasFavorite) return 1;
          
          // If both or neither have favorites, maintain original order
          return 0;
        })
        .map((game, gameIndex) => {
          const gameBookmakers = getBookmakersForGame(game, globalActiveMarket);
          const gameHasFavorite = isFavoriteTeam(game.home_team) || isFavoriteTeam(game.away_team);

          return (
            <Card 
              key={gameIndex} 
              elevation={gameHasFavorite ? 3 : 1} 
              sx={{ 
                mb: 2, 
                overflow: 'hidden',
                border: gameHasFavorite ? '2px solid' : 'none',
                borderColor: gameHasFavorite ? 'warning.main' : 'transparent',
                position: 'relative'
              }}
            >
              {/* Game Header */}
              {/*}
              <Box
                sx={{
                  backgroundColor: 'grey.50',
                  p: 2,
                  borderBottom: 1,
                  borderColor: 'divider'
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {game.away_team} @ {game.home_team}
                    </Typography>
                    <Box display="flex" gap={1}>
                      <Chip
                        icon={<SportIcon sportTitle={game.sport_title} fontSize="small" />}
                        label={game.sport_title}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                      <Chip
                        label={new Date(game.commence_time).toLocaleDateString()}
                        size="small"
                        variant="outlined"
                      />
                      <Chip
                        label={new Date(game.commence_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
            */}
              {/* Odds Table */}
              <TableContainer>
                <Table size="small" sx={{ minWidth: 300 }}>
                  <TableHead>
                    <TableRow sx={{ 
                      backgroundColor: theme.palette.mode === 'dark' ? '#2d5a4a' : 'grey.100'
                    }}>
                      <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>
                        <Box display="flex" gap={1} alignItems="center">
                          <Chip
                            icon={<SportIcon sportTitle={game.sport_title} fontSize="small" />}
                            label={game.sport_title}
                            size="small"
                            sx={{
                              color: theme.palette.mode === 'dark' ? 'white' : 'text.primary',
                              borderColor: theme.palette.mode === 'dark' ? 'white' : 'grey.400',
                            }}
                            variant="outlined"
                          />
                          <Chip
                            label={new Date(game.commence_time).toLocaleDateString()}
                            size="small"
                            sx={{
                              color: theme.palette.mode === 'dark' ? 'white' : 'text.primary',
                              borderColor: theme.palette.mode === 'dark' ? 'white' : 'grey.400',
                            }}
                            variant="outlined"
                          />
                          <Chip
                            label={new Date(game.commence_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            size="small"
                            sx={{
                              color: theme.palette.mode === 'dark' ? 'white' : 'text.primary',
                              borderColor: theme.palette.mode === 'dark' ? 'white' : 'grey.400',
                            }}
                            variant="outlined"
                          />
                          {gameHasFavorite && (
                            <Chip
                              icon={<StarIcon />}
                              label="Favorite"
                              size="small"
                              color="warning"
                              variant="filled"
                            />
                          )}
                          <Tooltip title="Join game chat">
                            <IconButton
                              onClick={(e) => handleChatClick(game, e)}
                              size="small"
                            >
                              <ChatIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      {gameBookmakers.map((bookmaker) => (
                        <TableCell
                          key={bookmaker}
                          align="center"
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            px: 0.5,
                            minWidth: 80
                          }}
                        >
                          {bookmaker}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {renderTableContent(game, gameIndex, globalActiveMarket, gameBookmakers)}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          );
        })}

      {/* Game Chat Dialog */}
      <GameChatDialog
        open={chatDialogOpen}
        onClose={handleCloseChatDialog}
        game={selectedGame}
      />
    </Box>
  );
};

export default LiveGamesList;
